import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { Column } from '@app/shared/modules/smash-data-table/smash-data-table/smash-data-table.component';
import { Team } from '@app/shared/models/team';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { UserPromotionActions } from './actions';
import { ModalConfirmComponent } from './modals/modal-confirm/modal-confirm.component';
import * as fromUserPromotions from './reducers/promotion.reducer';

@Component({
  selector: 'app-promotion',
  templateUrl: './promotion.component.html',
  styleUrls: ['./promotion.component.scss']
})
export class PromotionComponent implements OnDestroy {

  promotions: any[];
  promotionsLoading$: Observable<boolean>;
  promotionToCreate$: Observable<any>;
  selectedTeam$: Observable<Team>;
  searchPromotion: string;
  team: Team;
  confirmTitle: string;
  confirmHint: string;
  confirmButton: string;
  confirmAction: string;

  columnsEnabled: Column[] = [
    { id: 'title', colName: 'PREMIUM_PROMOTION_TABLE_LABEL_TITLE', type: 'string', removable: false, searchable: true },
    {
      id: 'status', colName: 'PREMIUM_PROMOTION_TABLE_LABEL_STATUS', type: 'string', removable: false,
      translations: {
        Enabled: 'PREMIUM_PROMOTION_ENABLED',
        Disabled: 'PREMIUM_PROMOTION_DISABLED',
      }
    },
    {
      id: 'target', colName: 'PREMIUM_PROMOTION_TABLE_LABEL_TARGET', type: 'string', removable: false, translations: {
        External: 'PREMIUM_PROMOTION_OPTION_EXTERNAL',
        Internal: 'PREMIUM_PROMOTION_OPTION_INTERNAL'
      }
    },
    {
      id: 'language', colName: 'PREMIUM_PROMOTION_TABLE_LABEL_LANGUAGE', type: 'string', removable: false, searchable: true,
    },
  ];

  targets = [
    { value: 'External', label: 'PREMIUM_PROMOTION_OPTION_EXTERNAL' },
    { value: 'Internal', label: 'PREMIUM_PROMOTION_OPTION_INTERNAL' }
  ];

  status = {
    Enabled: 'PREMIUM_PROMOTION_ENABLED',
    Disabled: 'PREMIUM_PROMOTION_DISABLED'
  };

  subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private readonly store$: Store<fromRoot.State & fromUserPromotions.State>,
    private route: ActivatedRoute,
    private dialog: DialogService,
  ) {

    this.selectedTeam$ = this.store$.pipe(select(fromRoot.getActiveTeam));
    this.promotionsLoading$ = this.store$.pipe(select(fromUserPromotions.getPromotionsLoading));

    this.subscriptions.push(
      this.store$.select(fromRoot.getActiveTeam).subscribe(team => {
        if (!!team) {
          this.team = team;
          this.store$.dispatch(UserPromotionActions.loadTeamPromotions({ teamId: team.id }));
        }
      }),
      this.store$.select(fromUserPromotions.getPromotionsLoaded).subscribe(promotions => {
        this.promotions = promotions;
      }),
    )

  }

  refresh() {
    this.store$.dispatch(UserPromotionActions.loadTeamPromotions({ teamId: this.team.id }));
  }

  openModalConfirm(promotion, action) {
    const confirmAction = action;
    switch (action) {
      case 'enable':
        this.store$.dispatch(UserPromotionActions.selectPromotionToUpdate({ promotion: { ...promotion, status: 'Enabled' } }));
        this.dialog.open(ModalConfirmComponent, {
          enableClose: true,
          closeButton: false,
          size: 'sm',
          data: {
            confirmAction,
            confirmTitle: 'PREMIUM_PROMOTION_MODAL_CONFIRM_ACTIVATION_TITLE',
            confirmHint: 'PREMIUM_PROMOTION_MODAL_CONFIRM_ACTIVATION_DESCRIPTION',
            confirmButton: 'PREMIUM_PROMOTION_MODAL_CONFIRM_ACTIVATION_BUTTON_LABEL_CONFIRM',
          }
        });
        break;
      case 'disable':
        this.store$.dispatch(UserPromotionActions.selectPromotionToUpdate({ promotion: { ...promotion, status: 'Disabled' } }));
        this.dialog.open(ModalConfirmComponent, {
          enableClose: true,
          closeButton: false,
          size: 'sm',
          data: {
            confirmAction,
            confirmTitle: 'PREMIUM_PROMOTION_MODAL_CONFIRM_DESACTIVATION_TITLE',
            confirmHint: 'PREMIUM_PROMOTION_MODAL_CONFIRM_DESACTIVATION_DESCRIPTION',
            confirmButton: 'PREMIUM_PROMOTION_MODAL_CONFIRM_DESACTIVATION_BUTTON_LABEL_CONFIRM',
          }
        });
        break;
      case 'delete':
        this.store$.dispatch(UserPromotionActions.selectPromotionToDelete({ promotion }));
        this.dialog.open(ModalConfirmComponent, {
          enableClose: true,
          closeButton: false,
          size: 'sm',
          data: {
            confirmAction,
            confirmTitle: 'PREMIUM_PROMOTION_MODAL_CONFIRM_DELETE_TITLE',
            confirmHint: 'PREMIUM_PROMOTION_MODAL_CONFIRM_DELETE_DESCRIPTION',
            confirmButton: 'PREMIUM_PROMOTION_MODAL_CONFIRM_DELETE_BUTTON_LABEL_CONFIRM',
          }
        });
        break;
    }
  }

  onConfirmDelete() {
    this.store$.dispatch(UserPromotionActions.deletePromotion());
  }

  updatePromotion(promotionId) {
    this.route.params.subscribe(params => {
      this.router.navigateByUrl('/account/team/' + params.id + '/promotions/update/' + promotionId);
    });
  }

  createPromotion() {
    this.route.params.subscribe(params => {
      this.router.navigateByUrl('/account/team/' + params.id + '/promotions/create');
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }
}
